import {
  ESPORTS_ACTIVE_BOOKS,
  normalizeSportsbookId,
  type SportsbookId,
} from "@/lib/sportsbook-registry";
import { getBooksWithFieldGroups } from "@/components/optimizer/sportsbook-field-groups";
import { WHALES_COLUMN, type ComparisonColumnId } from "@/lib/whales";

export const SPORTSBOOK_PREFERENCES_VERSION = 1 as const;
const ESPORTS_MULTI_OVERRIDE_VERSION = 2 as const;
export const GLOBAL_SPORTSBOOK_FILTER_NAME = "global";
export const TOOL_SPORTSBOOK_FILTER_PREFIX = "tool:";
export const MAX_SPORTSBOOK_PREFERENCE_IDS = 100;
export const DEFAULT_GLOBAL_SPORTSBOOK_ID: SportsbookId = "fanduel";

export const CHART_BOOKS_FILTER_NAME = "chart-books";
export const CHART_BOOKS_PREFERENCE_VERSION = 4 as const;
const CHART_BOOK_ID = /^[a-z0-9_-]{1,128}$/;
export const OPTIMIZER_COMPARISON_ORDER_FILTER_NAME = "optimizer-comparison-order";
export const MAX_OPTIMIZER_COMPARISON_ORDER_IDS = MAX_SPORTSBOOK_PREFERENCE_IDS;

export const SPORTSBOOK_SURFACE = {
  ODDS_SCREEN: "odds-screen",
  OPTIMIZER: "optimizer",
  OPTIMIZER_FANTASY_PROPS: "optimizer-fantasy-props",
  ESPORTS: "esports-discrepancies",
  EV_SNIPER: "ev-sniper",
  WHALE_WATCH: "whale-watch",
  LOW_HOLD: "low-hold",
  ARBITRAGE: "arbitrage",
} as const;

export type SportsbookSurfaceId =
  (typeof SPORTSBOOK_SURFACE)[keyof typeof SPORTSBOOK_SURFACE];

export interface GlobalSportsbookProfileAll {
  v: typeof SPORTSBOOK_PREFERENCES_VERSION;
  mode: "all";
  defaultBookId: SportsbookId;
  overrideEpoch: string;
}

export interface GlobalSportsbookProfileCustom {
  v: typeof SPORTSBOOK_PREFERENCES_VERSION;
  mode: "custom";
  bookIds: SportsbookId[];
  defaultBookId: SportsbookId;
  overrideEpoch: string;
}

export type GlobalSportsbookProfile =
  | GlobalSportsbookProfileAll
  | GlobalSportsbookProfileCustom;

export interface MultiSportsbookOverride {
  v:
    | typeof SPORTSBOOK_PREFERENCES_VERSION
    | typeof ESPORTS_MULTI_OVERRIDE_VERSION;
  epoch: string;
  kind: "multi";
  addedBookIds: SportsbookId[];
  removedBookIds: SportsbookId[];
}

export interface SingleSportsbookOverride {
  v: typeof SPORTSBOOK_PREFERENCES_VERSION;
  epoch: string;
  kind: "single";
  bookId: SportsbookId;
}

export type ToolSportsbookOverride =
  | MultiSportsbookOverride
  | SingleSportsbookOverride;

export type SportsbookPreferencesStatus = "loading" | "ready" | "unavailable";

export interface SportsbookPreferencesState {
  profile: GlobalSportsbookProfile | null;
  overrides: Partial<Record<SportsbookSurfaceId, ToolSportsbookOverride>>;
  chartBooks?: ChartBooksState;
  comparisonOrder?: OptimizerComparisonOrderState;
}

export interface ChartBooksPreference {
  v: typeof CHART_BOOKS_PREFERENCE_VERSION;
  hiddenBookIds: string[];
}

export type ChartBooksState =
  | { kind: "missing" }
  | { kind: "supported"; value: ChartBooksPreference }
  | { kind: "unsupported"; raw: unknown };

export interface OptimizerComparisonOrderPreference {
  v: typeof SPORTSBOOK_PREFERENCES_VERSION;
  bookIds: ComparisonColumnId[];
  hiddenIds?: ComparisonColumnId[];
}

export type OptimizerComparisonOrderState =
  | { kind: "missing" }
  | { kind: "supported"; value: OptimizerComparisonOrderPreference }
  | { kind: "unsupported"; raw: unknown };

export interface SportsbookPreferencesBootstrap extends SportsbookPreferencesState {
  status: Exclude<SportsbookPreferencesStatus, "loading">;
  userId: string | null;
}

export interface SportsbookPreferencesRow {
  filter_name: string;
  filter_value: unknown;
}

export interface SportsbookSurfacePolicy {
  id: SportsbookSurfaceId;
  kind: "multi" | "single";
  supportedBookIds: readonly SportsbookId[];
  defaultEligibleBookIds: readonly SportsbookId[];
  preferredBookIds: readonly SportsbookId[];
  analyticalBookIds: readonly SportsbookId[];
  wireBookIds: Readonly<Partial<Record<SportsbookId, string>>>;
}

export const GLOBAL_ACTIONABLE_BOOK_IDS: readonly SportsbookId[] = Object.freeze([
  "prizepicks", "underdog", "sleeper", "fliff", "fanduel", "draftkings",
  "betmgm", "pinnacle", "caesars", "circa",
  "fanatics", "hardrock", "betr", "novig", "prophetx", "dabble",
  "parlayplay", "kalshi", "polymarket", "rebet",
  "draftkingspredictions", "kambi",
  "betonline", "propbuilder", "bovada", "dkpick6", "betrsportsbook",
  "shuffle", "sportzino", "sportsbettingag",
  "tailgate",
  "sleeperpredict",
  "courtside", "boomsports", "chalkboardpicks", "chalkboardssp", "smacktok", "fourcx", "fourcasters", "sxbet", "bet105", "onyxodds",
]);

const GLOBAL_ACTIONABLE_BOOK_SET = new Set<SportsbookId>(GLOBAL_ACTIONABLE_BOOK_IDS);
const OPTIMIZER_BOOKS: readonly SportsbookId[] = [
  "pinnacle", "circa", "kalshi", "novig", "prophetx", "polymarket",
  "draftkings", "fanduel", "caesars", "hardrock", "rebet",
  "fliff", "betmgm", "parlayplay", "fanatics",
  "draftkingspredictions", "kambi", "betonline",
  "propbuilder", "bovada", "betrsportsbook",
  "shuffle", "sportzino", "sportsbettingag",
  "tailgate",
  "sleeperpredict",
  "courtside", "sleeper", "boomsports", "chalkboardssp", "chalkboardpicks", "smacktok", "fourcx", "fourcasters", "sxbet", "bet105", "onyxodds",
];

const OPTIMIZER_FANTASY_PROPS_BOOKS: readonly SportsbookId[] = [
  "prizepicks", "underdog", "dabble", "dkpick6", "betr",
];

const OPTIMIZER_ANALYTICAL_BOOK_IDS: readonly SportsbookId[] = Object.freeze(
  getBooksWithFieldGroups(),
);

const MULTI_BOOK_TOOL_BOOKS: readonly SportsbookId[] = [
  "draftkings", "fanduel", "betmgm", "caesars", "fanatics", "novig",
  "prophetx", "dabble", "fliff", "betr", "pinnacle", "circa",
  "parlayplay", "underdog", "sleeper", "hardrock", "kalshi",
  "polymarket", "rebet", "prizepicks", "kambi", "betonline",
  "propbuilder", "draftkingspredictions", "bovada",
  "betrsportsbook", "shuffle", "sportzino",
  "sportsbettingag", "dkpick6", "tailgate",
  "sleeperpredict",
  "courtside", "boomsports", "chalkboardpicks", "chalkboardssp", "smacktok", "fourcx", "fourcasters", "sxbet", "bet105", "onyxodds",
];

const PICK_EM_BOOK_IDS = new Set<SportsbookId>([
  "prizepicks",
  "underdog",
  "sleeper",
  "betr",
  "dabble",
  "dkpick6",
  "parlayplay",
  "boomsports",
  "chalkboardpicks",
  "smacktok",
]);

const ARB_PAIRABLE_BOOKS: readonly SportsbookId[] = MULTI_BOOK_TOOL_BOOKS.filter(
  (id) => !PICK_EM_BOOK_IDS.has(id) && id !== "sleeperpredict",
);

const ALL_ACTIONABLE = GLOBAL_ACTIONABLE_BOOK_IDS;
const EV_SNIPER_ANALYTICAL_BOOKS: readonly SportsbookId[] = [
  ...MULTI_BOOK_TOOL_BOOKS,
];
const WHALE_WATCH_ANALYTICAL_BOOKS: readonly SportsbookId[] = [
  "pinnacle",
  "novig",
  "prophetx",
  "fourcx",
  "fourcasters",
  "sxbet",
  "kalshi",
  "polymarket",
];

export const SPORTSBOOK_SURFACE_POLICIES: Readonly<
  Record<SportsbookSurfaceId, SportsbookSurfacePolicy>
> = Object.freeze({
  [SPORTSBOOK_SURFACE.ODDS_SCREEN]: createMultiPolicy(
    SPORTSBOOK_SURFACE.ODDS_SCREEN,
    ALL_ACTIONABLE,
    ALL_ACTIONABLE,
    ALL_ACTIONABLE,
  ),
  [SPORTSBOOK_SURFACE.OPTIMIZER]: createSinglePolicy(
    SPORTSBOOK_SURFACE.OPTIMIZER,
    OPTIMIZER_BOOKS,
    ["novig"],
    OPTIMIZER_ANALYTICAL_BOOK_IDS,
  ),
  [SPORTSBOOK_SURFACE.OPTIMIZER_FANTASY_PROPS]: createSinglePolicy(
    SPORTSBOOK_SURFACE.OPTIMIZER_FANTASY_PROPS,
    OPTIMIZER_FANTASY_PROPS_BOOKS,
    ["prizepicks"],
    OPTIMIZER_ANALYTICAL_BOOK_IDS,
  ),
  [SPORTSBOOK_SURFACE.ESPORTS]: createMultiPolicy(
    SPORTSBOOK_SURFACE.ESPORTS,
    ESPORTS_ACTIVE_BOOKS,
  ),
  [SPORTSBOOK_SURFACE.EV_SNIPER]: createMultiPolicy(
    SPORTSBOOK_SURFACE.EV_SNIPER,
    MULTI_BOOK_TOOL_BOOKS,
    MULTI_BOOK_TOOL_BOOKS,
    EV_SNIPER_ANALYTICAL_BOOKS,
  ),
  [SPORTSBOOK_SURFACE.WHALE_WATCH]: createMultiPolicy(
    SPORTSBOOK_SURFACE.WHALE_WATCH,
    ALL_ACTIONABLE,
    ALL_ACTIONABLE,
    WHALE_WATCH_ANALYTICAL_BOOKS,
  ),
  [SPORTSBOOK_SURFACE.LOW_HOLD]: createMultiPolicy(
    SPORTSBOOK_SURFACE.LOW_HOLD,
    ARB_PAIRABLE_BOOKS,
  ),
  [SPORTSBOOK_SURFACE.ARBITRAGE]: createMultiPolicy(
    SPORTSBOOK_SURFACE.ARBITRAGE,
    ARB_PAIRABLE_BOOKS.filter((id) => id !== "pinnacle"),
  ),
});

function createMultiPolicy(
  id: SportsbookSurfaceId,
  supportedBookIds: readonly SportsbookId[],
  defaultEligibleBookIds = supportedBookIds,
  analyticalBookIds: readonly SportsbookId[] = [],
): SportsbookSurfacePolicy {
  const allBookIds = Array.from(new Set([...supportedBookIds, ...analyticalBookIds]));
  return {
    id,
    kind: "multi",
    supportedBookIds,
    defaultEligibleBookIds,
    preferredBookIds: [],
    analyticalBookIds,
    wireBookIds: Object.freeze(
      Object.fromEntries(allBookIds.map((bookId) => [bookId, bookId])),
    ),
  };
}

function createSinglePolicy(
  id: SportsbookSurfaceId,
  supportedBookIds: readonly SportsbookId[],
  preferredBookIds: readonly SportsbookId[],
  analyticalBookIds: readonly SportsbookId[] = [],
): SportsbookSurfacePolicy {
  const allBookIds = Array.from(new Set([...supportedBookIds, ...analyticalBookIds]));
  return {
    id,
    kind: "single",
    supportedBookIds,
    defaultEligibleBookIds: supportedBookIds,
    preferredBookIds,
    analyticalBookIds,
    wireBookIds: Object.freeze(
      Object.fromEntries(allBookIds.map((bookId) => [bookId, bookId])),
    ),
  };
}

export function isSportsbookSurfaceId(value: string): value is SportsbookSurfaceId {
  return Object.hasOwn(SPORTSBOOK_SURFACE_POLICIES, value);
}

export function getSportsbookSurfacePolicy(
  surface: SportsbookSurfaceId,
): SportsbookSurfacePolicy {
  return SPORTSBOOK_SURFACE_POLICIES[surface];
}

export function getToolSportsbookFilterName(surface: SportsbookSurfaceId): string {
  return `${TOOL_SPORTSBOOK_FILTER_PREFIX}${surface}`;
}

export function parseToolSportsbookFilterName(
  filterName: string,
): SportsbookSurfaceId | null {
  if (!filterName.startsWith(TOOL_SPORTSBOOK_FILTER_PREFIX)) return null;
  const surface = filterName.slice(TOOL_SPORTSBOOK_FILTER_PREFIX.length);
  return isSportsbookSurfaceId(surface) ? surface : null;
}

export function normalizeActionableSportsbookIds(
  values: readonly unknown[],
): SportsbookId[] {
  const output: SportsbookId[] = [];
  const seen = new Set<SportsbookId>();

  for (const value of values.slice(0, MAX_SPORTSBOOK_PREFERENCE_IDS + 1)) {
    if (typeof value !== "string") continue;
    const normalized = normalizeSportsbookId(value);
    if (!normalized || !GLOBAL_ACTIONABLE_BOOK_SET.has(normalized) || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    output.push(normalized);
  }

  return output;
}

export function normalizeSportsbookPreferenceInput(
  values: readonly unknown[],
): SportsbookId[] | null {
  if (values.length > MAX_SPORTSBOOK_PREFERENCE_IDS) return null;

  const output: SportsbookId[] = [];
  const seen = new Set<SportsbookId>();
  for (const value of values) {
    if (typeof value !== "string") return null;
    const normalized = normalizeSportsbookId(value);
    if (!normalized || !GLOBAL_ACTIONABLE_BOOK_SET.has(normalized)) return null;
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    output.push(normalized);
  }
  return output;
}

export function parseGlobalSportsbookProfile(
  value: unknown,
): GlobalSportsbookProfile | null {
  if (!isRecord(value) || value.v !== SPORTSBOOK_PREFERENCES_VERSION) return null;
  if (!isValidEpoch(value.overrideEpoch)) return null;

  if (
    value.mode === "all" &&
    (hasExactKeys(value, ["v", "mode", "defaultBookId", "overrideEpoch"]) ||
      hasExactKeys(value, ["v", "mode", "overrideEpoch"]))
  ) {
    const defaultBookId = normalizeStoredBookId(value.defaultBookId) ?? DEFAULT_GLOBAL_SPORTSBOOK_ID;
    return {
      v: SPORTSBOOK_PREFERENCES_VERSION,
      mode: "all",
      defaultBookId,
      overrideEpoch: value.overrideEpoch,
    };
  }

  if (
    value.mode !== "custom" ||
    (!hasExactKeys(value, ["v", "mode", "bookIds", "defaultBookId", "overrideEpoch"]) &&
      !hasExactKeys(value, ["v", "mode", "bookIds", "overrideEpoch"])) ||
    !Array.isArray(value.bookIds) ||
    value.bookIds.length === 0 ||
    value.bookIds.length > MAX_SPORTSBOOK_PREFERENCE_IDS
  ) {
    return null;
  }

  const bookIds = normalizeActionableSportsbookIds(value.bookIds);
  if (bookIds.length === 0) return null;
  const defaultBookId = normalizeStoredBookId(value.defaultBookId) ?? bookIds[0];
  if (!bookIds.includes(defaultBookId)) return null;

  return {
    v: SPORTSBOOK_PREFERENCES_VERSION,
    mode: "custom",
    bookIds,
    defaultBookId,
    overrideEpoch: value.overrideEpoch,
  };
}

export function parseToolSportsbookOverride(
  value: unknown,
  surface?: SportsbookSurfaceId,
): ToolSportsbookOverride | null {
  if (
    !isRecord(value) ||
    (value.v !== SPORTSBOOK_PREFERENCES_VERSION &&
      value.v !== ESPORTS_MULTI_OVERRIDE_VERSION)
  ) {
    return null;
  }
  if (!isValidEpoch(value.epoch)) return null;
  const policy = surface ? getSportsbookSurfacePolicy(surface) : null;

  if (value.kind === "single") {
    if (value.v !== SPORTSBOOK_PREFERENCES_VERSION) return null;
    if (!hasExactKeys(value, ["v", "epoch", "kind", "bookId"])) return null;
    const bookId = normalizeStoredBookId(value.bookId);
    if (!bookId || (policy && (policy.kind !== "single" || !policy.supportedBookIds.includes(bookId)))) {
      return null;
    }
    return {
      v: SPORTSBOOK_PREFERENCES_VERSION,
      epoch: value.epoch,
      kind: "single",
      bookId,
    };
  }

  if (
    value.kind !== "multi" ||
    (value.v === ESPORTS_MULTI_OVERRIDE_VERSION &&
      surface !== SPORTSBOOK_SURFACE.ESPORTS) ||
    !hasExactKeys(value, ["v", "epoch", "kind", "addedBookIds", "removedBookIds"]) ||
    !Array.isArray(value.addedBookIds) ||
    !Array.isArray(value.removedBookIds) ||
    value.addedBookIds.length > MAX_SPORTSBOOK_PREFERENCE_IDS ||
    value.removedBookIds.length > MAX_SPORTSBOOK_PREFERENCE_IDS ||
    (policy && policy.kind !== "multi")
  ) {
    return null;
  }

  const addedBookIds = normalizeActionableSportsbookIds(value.addedBookIds);
  const removedBookIds = normalizeActionableSportsbookIds(value.removedBookIds.filter((id) => normalizeStoredBookId(id) === id))
    .filter((id) => !addedBookIds.includes(id));
  if (policy && [...addedBookIds, ...removedBookIds].some((id) => !policy.supportedBookIds.includes(id))) {
    return null;
  }

  return {
    v: value.v,
    epoch: value.epoch,
    kind: "multi",
    addedBookIds,
    removedBookIds,
  };
}

export function normalizeChartBooksInput(
  input: unknown,
): ChartBooksPreference | null {
  if (
    !isRecord(input) ||
    input.v !== CHART_BOOKS_PREFERENCE_VERSION ||
    !hasExactKeys(input, ["v", "hiddenBookIds"]) ||
    !Array.isArray(input.hiddenBookIds) ||
    input.hiddenBookIds.length > MAX_SPORTSBOOK_PREFERENCE_IDS
  ) {
    return null;
  }
  const hiddenBookIds: string[] = [];
  for (const rawBookId of input.hiddenBookIds) {
    const bookId = typeof rawBookId === "string" ? chartBookKey(rawBookId) : "";
    if (!bookId || hiddenBookIds.includes(bookId)) return null;
    hiddenBookIds.push(bookId);
  }
  return { v: CHART_BOOKS_PREFERENCE_VERSION, hiddenBookIds };
}

export function chartBookKey(book: string): string {
  return normalizeSportsbookId(book) || (CHART_BOOK_ID.test(book) ? book : "");
}

export function parseChartBooksRow(value: unknown): ChartBooksState {
  if (isRecord(value) && typeof value.v === "number" && value.v > CHART_BOOKS_PREFERENCE_VERSION) {
    return { kind: "unsupported", raw: value };
  }
  const parsed = normalizeChartBooksInput(
    isRecord(value) && Array.isArray(value.hiddenBookIds)
      ? { ...value, hiddenBookIds: [...new Set(value.hiddenBookIds.map((id) => (typeof id === "string" ? chartBookKey(id) || id : id)))] }
      : value,
  );
  return parsed ? { kind: "supported", value: parsed } : { kind: "missing" };
}

export function getChartBooksState(
  state: SportsbookPreferencesState,
): ChartBooksState {
  return state.chartBooks ?? { kind: "missing" };
}

function comparisonColumnId(raw: unknown, fieldBackedBookIds: ReadonlySet<SportsbookId>): ComparisonColumnId | null {
  if (raw === WHALES_COLUMN) return WHALES_COLUMN;
  if (typeof raw !== "string") return null;
  const bookId = normalizeSportsbookId(raw);
  return bookId && fieldBackedBookIds.has(bookId) ? bookId : null;
}

function comparisonOrderOf(bookIds: ComparisonColumnId[], hiddenIds: ComparisonColumnId[]): OptimizerComparisonOrderPreference {
  return hiddenIds.length > 0 ? { v: SPORTSBOOK_PREFERENCES_VERSION, bookIds, hiddenIds } : { v: SPORTSBOOK_PREFERENCES_VERSION, bookIds };
}

function isComparisonOrderShape(input: unknown): input is { v: unknown; bookIds: unknown[]; hiddenIds?: unknown } {
  return (
    isRecord(input) &&
    input.v === SPORTSBOOK_PREFERENCES_VERSION &&
    (hasExactKeys(input, ["v", "bookIds"]) || hasExactKeys(input, ["v", "bookIds", "hiddenIds"])) &&
    Array.isArray(input.bookIds) &&
    input.bookIds.length > 0 &&
    input.bookIds.length <= MAX_OPTIMIZER_COMPARISON_ORDER_IDS &&
    (input.hiddenIds === undefined || (Array.isArray(input.hiddenIds) && input.hiddenIds.length <= MAX_OPTIMIZER_COMPARISON_ORDER_IDS))
  );
}

export function normalizeOptimizerComparisonOrderInput(
  input: unknown,
): OptimizerComparisonOrderPreference | null {
  if (!isComparisonOrderShape(input)) return null;
  const fieldBackedBookIds = new Set(OPTIMIZER_ANALYTICAL_BOOK_IDS);
  const strict = (raw: readonly unknown[]): ComparisonColumnId[] | null => {
    const ids: ComparisonColumnId[] = [];
    for (const value of raw) {
      const id = comparisonColumnId(value, fieldBackedBookIds);
      if (id === null || ids.includes(id)) return null;
      ids.push(id);
    }
    return ids;
  };
  const bookIds = strict(input.bookIds);
  const hiddenIds = strict(Array.isArray(input.hiddenIds) ? input.hiddenIds : []);
  return bookIds === null || hiddenIds === null ? null : comparisonOrderOf(bookIds, hiddenIds);
}

export function parseOptimizerComparisonOrderRow(
  value: unknown,
): OptimizerComparisonOrderState {
  if (
    isRecord(value) &&
    typeof value.v === "number" &&
    value.v !== SPORTSBOOK_PREFERENCES_VERSION
  ) {
    return { kind: "unsupported", raw: value };
  }
  const parsed = normalizeStoredOptimizerComparisonOrder(value);
  return parsed ? { kind: "supported", value: parsed } : { kind: "missing" };
}

function normalizeStoredOptimizerComparisonOrder(
  input: unknown,
): OptimizerComparisonOrderPreference | null {
  if (!isComparisonOrderShape(input)) return null;
  const fieldBackedBookIds = new Set(OPTIMIZER_ANALYTICAL_BOOK_IDS);
  const lenient = (raw: readonly unknown[]): ComparisonColumnId[] => {
    const ids: ComparisonColumnId[] = [];
    for (const value of raw) {
      const id = comparisonColumnId(value, fieldBackedBookIds);
      if (id !== null && !ids.includes(id)) ids.push(id);
    }
    return ids;
  };
  const bookIds = lenient(input.bookIds);
  return bookIds.length > 0 ? comparisonOrderOf(bookIds, lenient(Array.isArray(input.hiddenIds) ? input.hiddenIds : [])) : null;
}

export function getOptimizerComparisonOrderState(
  state: SportsbookPreferencesState,
): OptimizerComparisonOrderState {
  return state.comparisonOrder ?? { kind: "missing" };
}

export function parseSportsbookPreferencesRows(
  rows: readonly SportsbookPreferencesRow[],
  userId: string | null,
): SportsbookPreferencesBootstrap {
  let profile: GlobalSportsbookProfile | null = null;
  const overrides: SportsbookPreferencesState["overrides"] = {};
  let chartBooks: ChartBooksState = { kind: "missing" };
  let comparisonOrder: OptimizerComparisonOrderState = { kind: "missing" };

  for (const row of rows) {
    if (row.filter_name === GLOBAL_SPORTSBOOK_FILTER_NAME) {
      const parsed = parseGlobalSportsbookProfile(row.filter_value);
      if (parsed && !profile) {
        profile = parsed;
      }
      continue;
    }

    if (row.filter_name === CHART_BOOKS_FILTER_NAME) {
      if (chartBooks.kind === "missing") {
        chartBooks = parseChartBooksRow(row.filter_value);
      }
      continue;
    }

    if (row.filter_name === OPTIMIZER_COMPARISON_ORDER_FILTER_NAME) {
      if (comparisonOrder.kind === "missing") {
        comparisonOrder = parseOptimizerComparisonOrderRow(row.filter_value);
      }
      continue;
    }

    const surface = parseToolSportsbookFilterName(row.filter_name);
    if (!surface) {
      continue;
    }
    const parsed = parseToolSportsbookOverride(row.filter_value, surface);
    if (parsed && !overrides[surface]) {
      overrides[surface] = parsed;
    }
  }

  return {
    status: "ready",
    userId,
    profile,
    overrides,
    chartBooks,
    comparisonOrder,
  };
}

export function getGlobalSportsbookIds(
  profile: GlobalSportsbookProfile | null,
): SportsbookId[] {
  return profile?.mode === "custom"
    ? [...profile.bookIds]
    : [...GLOBAL_ACTIONABLE_BOOK_IDS];
}

export function getGlobalDefaultSportsbookId(
  profile: GlobalSportsbookProfile | null,
): SportsbookId {
  return profile?.defaultBookId ?? DEFAULT_GLOBAL_SPORTSBOOK_ID;
}

export function getSurfaceDefaultBookIds(
  profile: GlobalSportsbookProfile | null,
  surface: SportsbookSurfaceId,
): SportsbookId[] {
  const policy = getSportsbookSurfacePolicy(surface);
  if (
    !profile ||
    surface === SPORTSBOOK_SURFACE.ESPORTS ||
    surface === SPORTSBOOK_SURFACE.ODDS_SCREEN ||
    surface === SPORTSBOOK_SURFACE.ARBITRAGE ||
    surface === SPORTSBOOK_SURFACE.WHALE_WATCH
  ) {
    return [...policy.defaultEligibleBookIds];
  }

  return getProfileEligibleBookIds(profile, policy);
}

export function getSingleSportsbookDefaults(
  profile: GlobalSportsbookProfile | null,
  surface: SportsbookSurfaceId,
): SportsbookId[] {
  const policy = getSportsbookSurfacePolicy(surface);
  const everyBook = [...new Set([...policy.preferredBookIds.filter((id) => policy.supportedBookIds.includes(id)), ...policy.supportedBookIds])];
  if (!profile) return everyBook;
  const eligible = getSurfaceDefaultBookIds(profile, surface);
  if (eligible.length === 0) return everyBook;
  return eligible.includes(profile.defaultBookId) ? [...new Set([profile.defaultBookId, ...eligible])] : eligible;
}

export function getProfileBookIdsForSurface(
  profile: GlobalSportsbookProfile | null,
  surface: SportsbookSurfaceId,
): SportsbookId[] {
  const policy = getSportsbookSurfacePolicy(surface);
  return profile ? getProfileEligibleBookIds(profile, policy) : [...policy.defaultEligibleBookIds];
}

function getProfileEligibleBookIds(
  profile: GlobalSportsbookProfile,
  policy: SportsbookSurfacePolicy,
): SportsbookId[] {
  const eligibleBookIds = new Set(policy.defaultEligibleBookIds);
  return getGlobalSportsbookIds(profile).filter((id) => eligibleBookIds.has(id));
}

export function getEffectiveMultiSportsbookIds(
  profile: GlobalSportsbookProfile | null,
  override: ToolSportsbookOverride | null | undefined,
  surface: SportsbookSurfaceId,
): SportsbookId[] {
  const policy = getSportsbookSurfacePolicy(surface);
  if (policy.kind !== "multi") {
    throw new Error(`${surface} is not a multi-book sportsbook surface`);
  }

  const base = getSurfaceDefaultBookIds(profile, surface);
  if (
    !profile ||
    !override ||
    override.kind !== "multi" ||
    override.epoch !== profile.overrideEpoch
  ) {
    return base;
  }

  const removed = new Set(override.removedBookIds);
  if (surface === SPORTSBOOK_SURFACE.ESPORTS) {
    const overrideBase = override.v === ESPORTS_MULTI_OVERRIDE_VERSION
      ? base
      : getProfileEligibleBookIds(profile, policy);
    const selected = new Set([
      ...overrideBase.filter((id) => !removed.has(id)),
      ...override.addedBookIds,
    ]);
    return policy.defaultEligibleBookIds.filter((id) => selected.has(id));
  }
  const effective = base.filter((id) => !removed.has(id));
  return effective;
}

export interface SingleSportsbookResolution {
  bookId: SportsbookId;
  source: "explicit" | "remembered" | "fallback" | "legacy-default";
  unavailableBookId: SportsbookId | null;
}

export function resolveSingleSportsbookSelection(
  profile: GlobalSportsbookProfile | null,
  override: ToolSportsbookOverride | null | undefined,
  surface: SportsbookSurfaceId,
  explicitBookId?: string | null,
): SingleSportsbookResolution {
  const policy = getSportsbookSurfacePolicy(surface);
  if (policy.kind !== "single") {
    throw new Error(`${surface} is not a single-book sportsbook surface`);
  }

  const explicit = normalizeSportsbookId(explicitBookId);
  if (!profile) {
    if (explicit && policy.supportedBookIds.includes(explicit)) {
      return { bookId: explicit, source: "explicit", unavailableBookId: null };
    }
    return {
      bookId: getSingleSportsbookDefaults(null, surface)[0],
      source: "legacy-default",
      unavailableBookId: null,
    };
  }

  if (explicit && policy.supportedBookIds.includes(explicit)) {
    return { bookId: explicit, source: "explicit", unavailableBookId: null };
  }

  const rememberedBookId =
    override?.kind === "single" && override.epoch === profile.overrideEpoch
      ? override.bookId
      : null;
  if (rememberedBookId && policy.supportedBookIds.includes(rememberedBookId)) {
    return { bookId: rememberedBookId, source: "remembered", unavailableBookId: null };
  }

  return {
    bookId: getSingleSportsbookDefaults(profile, surface)[0],
    source: "fallback",
    unavailableBookId: explicit || rememberedBookId || null,
  };
}

export function createMultiSportsbookOverride(
  profile: GlobalSportsbookProfile,
  surface: SportsbookSurfaceId,
  selectedBookIds: readonly unknown[],
): MultiSportsbookOverride | null {
  const policy = getSportsbookSurfacePolicy(surface);
  if (policy.kind !== "multi" || selectedBookIds.length > MAX_SPORTSBOOK_PREFERENCE_IDS) {
    return null;
  }

  const selected = normalizeSportsbookPreferenceInput(selectedBookIds);
  if (
    !selected ||
    selected.some((id) => !policy.supportedBookIds.includes(id))
  ) {
    return null;
  }

  const base = getSurfaceDefaultBookIds(profile, surface);
  const baseSet = new Set(base);
  const selectedSet = new Set(selected);
  if (selected.some((id) => !baseSet.has(id))) return null;
  const overrideVersion = surface === SPORTSBOOK_SURFACE.ESPORTS
    ? ESPORTS_MULTI_OVERRIDE_VERSION
    : SPORTSBOOK_PREFERENCES_VERSION;

  if (
    selected.length === base.length &&
    base.every((id) => selectedSet.has(id))
  ) {
    return {
      v: overrideVersion,
      epoch: profile.overrideEpoch,
      kind: "multi",
      addedBookIds: [],
      removedBookIds: [],
    };
  }

  return {
    v: overrideVersion,
    epoch: profile.overrideEpoch,
    kind: "multi",
    addedBookIds: [],
    removedBookIds: base.filter((id) => !selectedSet.has(id)),
  };
}

export function isEmptyMultiSportsbookOverride(
  override: MultiSportsbookOverride,
): boolean {
  return override.addedBookIds.length === 0 && override.removedBookIds.length === 0;
}

export function createSingleSportsbookOverride(
  profile: GlobalSportsbookProfile,
  surface: SportsbookSurfaceId,
  bookId: unknown,
): SingleSportsbookOverride | null {
  const policy = getSportsbookSurfacePolicy(surface);
  const normalized = normalizeStoredBookId(bookId);
  if (
    policy.kind !== "single" ||
    !normalized ||
    !policy.supportedBookIds.includes(normalized)
  ) {
    return null;
  }

  return {
    v: SPORTSBOOK_PREFERENCES_VERSION,
    epoch: profile.overrideEpoch,
    kind: "single",
    bookId: normalized,
  };
}

function normalizeStoredBookId(value: unknown): SportsbookId | null {
  if (typeof value !== "string") return null;
  const normalized = normalizeSportsbookId(value);
  return normalized && GLOBAL_ACTIONABLE_BOOK_SET.has(normalized) ? normalized : null;
}

function isValidEpoch(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 100;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  const keys = Object.keys(value);
  return keys.length === expected.length && keys.every((key) => expected.includes(key));
}
